import type { DateRange } from '@daypicker/react';
import { endOfYear, isSameDay, startOfYear } from 'date-fns';
import { Check, ChevronDown, type LucideIcon, Minus, Search, X } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { DateRangeCalendar, type DateRangePreset } from '@/components/ui/DateRangePicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';
import { getCategoryStyle } from './styles';
import { TOOLBAR_BUTTON_ACTIVE_CLASSNAME, TOOLBAR_BUTTON_CLASSNAME } from './toolbar-styles';
import type { TypeEntry } from './useChronologyFilters';

const COUNT_CLASSNAME = 'ml-auto shrink-0 pl-2 text-[11px] tabular-nums text-gray-400';

export function summarizeSelection(values: readonly string[]): string | null {
  if (values.length === 0) return null;
  return values.length === 1 ? values[0] : `${values[0]} +${values.length - 1}`;
}

export function summarizeDateRange(range: DateRange | undefined, locale: RegionalDateLocale): string | null {
  if (!range?.from) return null;
  const { from, to } = range;
  if (to && isSameDay(from, startOfYear(from)) && isSameDay(to, endOfYear(from))) {
    return String(from.getFullYear());
  }
  if (!to) return `From ${formatRegionalDate(from, locale)}`;
  if (isSameDay(from, to)) return formatRegionalDate(from, locale);
  return `${formatRegionalDate(from, locale)} – ${formatRegionalDate(to, locale)}`;
}

function CheckMark({ state }: { state: 'all' | 'some' | 'none' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border transition-colors',
        state === 'none' ? 'border-gray-300 bg-white' : 'border-gray-800 bg-gray-800 text-white',
      )}
    >
      {state === 'all' && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
      {state === 'some' && <Minus className="h-2.5 w-2.5" strokeWidth={3} />}
    </span>
  );
}

function PopoverSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-gray-100 px-3">
      <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <input
        type="text"
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 w-full bg-transparent text-[13px] text-gray-800 outline-hidden placeholder:text-gray-400"
      />
    </div>
  );
}

function PopoverFooter({ selectedCount, onClear }: { selectedCount: number; onClear: () => void }) {
  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-[11px]">
      <span className="text-gray-400">
        {selectedCount > 0 ? `${selectedCount} selected` : 'Nothing selected shows all events'}
      </span>
      {selectedCount > 0 && (
        <button type="button" onClick={onClear} className="font-medium text-gray-500 hover:text-gray-900">
          Clear
        </button>
      )}
    </div>
  );
}

interface FilterButtonProps {
  icon: LucideIcon;
  label: string;
  /** Summary of the applied value; its presence marks the filter as active. */
  value?: string | null;
  onClear?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentClassName?: string;
  /** Hover text for the trigger. */
  tooltip?: string;
  children: ReactNode;
}

export function FilterButton({
  icon: Icon,
  label,
  value,
  onClear,
  open,
  onOpenChange,
  contentClassName,
  tooltip,
  children,
}: FilterButtonProps) {
  const active = Boolean(value);
  const trigger = (
    <PopoverTrigger asChild>
      <button
        type="button"
        className={cn(
          'inline-flex h-full min-w-0 items-center gap-1.5 rounded-md pl-2.5 focus-visible:outline-hidden',
          active ? 'pr-1.5' : 'pr-2',
        )}
      >
        <Icon className={cn('h-3.5 w-3.5 shrink-0', active ? 'text-gray-600' : 'text-gray-400')} />
        {active ? (
          <>
            <span className="shrink-0 text-gray-500">{label}:</span>
            <span className="max-w-[13rem] truncate font-medium text-gray-900">{value}</span>
          </>
        ) : (
          <>
            <span>{label}</span>
            <ChevronDown className={cn('h-3 w-3 shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} />
          </>
        )}
      </button>
    </PopoverTrigger>
  );
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <div
        className={cn(
          TOOLBAR_BUTTON_CLASSNAME,
          'gap-0 px-0',
          (active || open) && TOOLBAR_BUTTON_ACTIVE_CLASSNAME,
        )}
      >
        {tooltip ? (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent side="top" className="text-xs font-normal">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          trigger
        )}
        {active && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`Clear ${label.toLowerCase()} filter`}
            title={`Clear ${label.toLowerCase()} filter`}
            className="mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-700"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <PopoverContent className={cn('w-[280px] p-0', contentClassName)} align="start" side="bottom" sideOffset={6}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

interface FacetListProps {
  counts: ReadonlyMap<string, number>;
  /** Display text for each id. Defaults to the id itself. */
  labels?: ReadonlyMap<string, string>;
  activeValues: ReadonlySet<string>;
  searchPlaceholder: string;
  onToggle: (value: string) => void;
  onClear: () => void;
}

/** Searchable checklist for a single facet (entities, documents, authors). */
export function FacetList({ counts, labels, activeValues, searchPlaceholder, onToggle, onClear }: FacetListProps) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const labelFor = (id: string) => labels?.get(id) ?? id;
  // Counts ignore this facet's own selection, so the order stays put while ticking.
  const entries = useMemo(() => {
    const labelForId = (id: string) => labels?.get(id) ?? id;
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || labelForId(a[0]).localeCompare(labelForId(b[0])))
      .filter(([id]) => !query || labelForId(id).toLowerCase().includes(query));
  }, [counts, labels, query]);

  return (
    <div>
      <PopoverSearch value={search} onChange={setSearch} placeholder={searchPlaceholder} />
      <div className="subtle-scrollbar max-h-[300px] overflow-y-auto py-1">
        {entries.length === 0 ? (
          <p className="px-3 py-3 text-xs text-gray-400">No matches.</p>
        ) : entries.map(([id, count]) => {
          const active = activeValues.has(id);
          const label = labelFor(id);
          return (
            <button
              type="button"
              key={id}
              aria-pressed={active}
              onClick={() => onToggle(id)}
              className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-gray-50"
            >
              <CheckMark state={active ? 'all' : 'none'} />
              <span title={label} className={cn('min-w-0 truncate', active ? 'text-gray-900' : 'text-gray-700')}>{label}</span>
              <span className={COUNT_CLASSNAME}>{count}</span>
            </button>
          );
        })}
      </div>
      <PopoverFooter selectedCount={activeValues.size} onClear={onClear} />
    </div>
  );
}

interface TagTreeProps {
  categories: readonly string[];
  tagsByCategory: ReadonlyMap<string, TypeEntry[]>;
  categoryCounts: ReadonlyMap<string, number>;
  typeCounts: ReadonlyMap<string, number>;
  colorByCategory?: ReadonlyMap<string, string | null>;
  selectedCount: number;
  categoryState: (category: string) => 'all' | 'some' | 'none';
  isTagSelected: (typeKey: string, category: string) => boolean;
  onToggleCategory: (category: string) => void;
  onToggleTag: (typeKey: string, category: string) => void;
  onOnlyTag: (typeKey: string, category: string) => void;
  onClear: () => void;
}

/** Tag categories with their tags nested underneath, as one checklist. */
export function TagTree({
  categories,
  tagsByCategory,
  categoryCounts,
  typeCounts,
  colorByCategory,
  selectedCount,
  categoryState,
  isTagSelected,
  onToggleCategory,
  onToggleTag,
  onOnlyTag,
  onClear,
}: TagTreeProps) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  const groups = categories
    .map(category => {
      const tags = tagsByCategory.get(category) ?? [];
      if (!query || category.toLowerCase().includes(query)) return { category, tags };
      const matching = tags.filter(tag => (tag.label || tag.key).toLowerCase().includes(query));
      return matching.length > 0 ? { category, tags: matching } : null;
    })
    .filter((group): group is { category: string; tags: TypeEntry[] } => group !== null);

  return (
    <div>
      <PopoverSearch value={search} onChange={setSearch} placeholder="Search tags..." />
      <div className="subtle-scrollbar max-h-[360px] overflow-y-auto py-1">
        {groups.length === 0 ? (
          <p className="px-3 py-3 text-xs text-gray-400">No matching tags.</p>
        ) : groups.map(({ category, tags }, index) => {
          const style = getCategoryStyle(category, colorByCategory);
          const state = categoryState(category);
          return (
            <div key={category} className={cn(index > 0 && 'mt-1 border-t border-gray-100 pt-1')}>
              <button
                type="button"
                aria-pressed={state === 'all' ? true : state === 'some' ? 'mixed' : false}
                onClick={() => onToggleCategory(category)}
                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-gray-50"
              >
                <CheckMark state={state} />
                <span className={cn('h-2 w-2 shrink-0 rounded-full', style.activeDot)} />
                <span className="min-w-0 truncate font-medium text-gray-800">{category}</span>
                <span className={COUNT_CLASSNAME}>{categoryCounts.get(category) ?? 0}</span>
              </button>
              {tags.map(tag => {
                const selected = isTagSelected(tag.key, tag.category);
                const label = tag.label || tag.key;
                return (
                  <div key={tag.key} className="group flex items-center transition-colors hover:bg-gray-50">
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => onToggleTag(tag.key, tag.category)}
                      className="flex min-w-0 flex-1 items-center gap-2.5 py-1.5 pl-8 text-left text-[13px]"
                    >
                      <CheckMark state={selected ? 'all' : 'none'} />
                      <span title={label} className={cn('min-w-0 truncate', selected ? 'text-gray-900' : 'text-gray-600')}>{label}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onOnlyTag(tag.key, tag.category)}
                      aria-label={`Show only ${label}`}
                      className="shrink-0 rounded-sm px-1.5 text-[11px] font-medium text-gray-400 opacity-0 transition-opacity hover:text-gray-800 focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      Only
                    </button>
                    <span className={cn(COUNT_CLASSNAME, 'ml-0 pr-3')}>{typeCounts.get(tag.key) ?? 0}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <PopoverFooter selectedCount={selectedCount} onClear={onClear} />
    </div>
  );
}

interface DateFilterContentProps {
  dateRange: DateRange | undefined;
  eventYears: number[];
  onChange: (range: DateRange | undefined) => void;
}

export function DateFilterContent({ dateRange, eventYears, onChange }: DateFilterContentProps) {
  const yearPresets = useMemo<DateRangePreset[]>(
    () => eventYears.map(year => {
      const date = new Date(year, 0, 1);
      return { id: String(year), label: String(year), range: { from: startOfYear(date), to: endOfYear(date) } };
    }),
    [eventYears],
  );

  return (
    <div className="w-[calc(100vw-2rem)] max-w-[31rem]">
      <DateRangeCalendar
        value={dateRange}
        onChange={onChange}
        initialMonth={dateRange?.from ?? (eventYears.length > 0 ? new Date(eventYears[0], 0, 1) : undefined)}
        customPresets={eventYears.length > 1 ? yearPresets : undefined}
      />
    </div>
  );
}
